import { CancelToken } from 'axios';

import { axiosInstance } from 'app/constants/axios-instance';
import { CustomerAddressList } from 'app/models/customer-address-list';
import { Address } from 'app/models/address';
import { AddressChange } from 'app/models/address-change';
import { CustomerList } from 'app/models/customer-list';
import { Customer } from 'app/models/customer';
import { transformJsonRequest } from 'app/utils/transform-json-request';
import { ApiAccessResultModel } from 'app/models/api-access-result-model';
import { CustomerChange } from 'app/models/customer-change';
import { CustomerProfileChange } from 'app/models/customer-profile-change';
import { CustomerActiveContractExtended } from 'app/models/customer-active-contract';
import { RoleType } from 'app/models/role-type';
import { Manager } from 'app/models/manager';
import { LocaleType } from 'app/models/locale-type';
import { CustomerApprovalInfo } from 'app/models/customer-approval-info';

export const getCustomers = (
  query: string | null,
  page: number,
  showArchived: boolean = false,
  cancelToken?: CancelToken,
  contractId?: string | null
) =>
  axiosInstance
    .get<CustomerList>('/customer', {
      params: { query, page, showArchived, contractId },
      cancelToken,
    })
    .then((response) => response.data);

export const getCustomer = (id: string, localeType: LocaleType) =>
  axiosInstance
    .get<Customer>(`/customer/${id}`, { params: { localeType } })
    .then((response) => response.data);

export const getCustomerApprovalInfo = (id: string, localeType: LocaleType) =>
  axiosInstance
    .get<CustomerApprovalInfo>(`/customer/${id}/approval-info`, { params: { localeType } })
    .then((response) => response.data);

export const getManagers = () =>
  axiosInstance
    .get<Customer[]>('/customer/managers')
    .then((response) => response.data);

export const getSupplyManager = (customerId: string) =>
  axiosInstance
    .get<Manager>(`/customer/${customerId}/supply-manager`)
    .then((response) => response.data)
    .catch((error) => {
      if (error.response?.status === 204) {
        return null;
      }
      throw error;
    });

export const updateCustomer = (id: string, customer: CustomerChange) =>
  axiosInstance.post(`/customer/${id}`, customer).then((response) => response.data);

export const updateCustomerInvoiceReports = (id: string, invoiceReports: string[] | null) =>
  axiosInstance
    .put<void>(`/customer/${id}/invoice-reports`, invoiceReports, {
      transformRequest: transformJsonRequest,
    })
    .then((response) => response.data);

export const updateCurrentCustomer = (customer: CustomerProfileChange) =>
  axiosInstance.put<void>('/customer/current', customer).then((response) => response.data);

export const updatePriceListNotifications = (customerId: string, priceListNotifications: boolean) =>
  axiosInstance
    .put<void>(`/customer/${customerId}/price-list-notifications`, priceListNotifications, {
      transformRequest: transformJsonRequest,
    })
    .then((response) => response.data);

export const updateOrderNotifications = (customerId: string, orderNotifications: boolean) =>
  axiosInstance
    .put<void>(`/customer/${customerId}/order-notifications`, orderNotifications, {
      transformRequest: transformJsonRequest,
    })
    .then((response) => response.data);

export const activateCustomer = (customerId: string, selectedContractId?: string | null) =>
  axiosInstance.post<void>(`/customer/${customerId}/activate`, { selectedContractId: selectedContractId ?? null });

export const deativateCustomer = (customerId: string) => axiosInstance.post<void>(`/customer/${customerId}/deactivate`);

export const archiveCustomer = (customerId: string) => axiosInstance.post<void>(`/customer/${customerId}/archive`);

export const unarchiveCustomer = (customerId: string) => axiosInstance.post<void>(`/customer/${customerId}/unarchive`);

export const getCustomerAddresses = (customerId: string, localeType: LocaleType) =>
  axiosInstance
    .get<CustomerAddressList>(`/customer/${customerId}/address`, { params: { localeType } })
    .then((response) => response.data);

export const createCustomerAddress = (customerId: string, address: AddressChange, localeType: LocaleType) =>
  axiosInstance
    .post<Address>(`/customer/${customerId}/address`, address, { params: { localeType } })
    .then((response) => response.data);

export const updateCustomerAddress = (customerId: string, id: number, address: AddressChange) =>
  axiosInstance.put<void>(`/customer/${customerId}/address/${id}`, address).then((response) => response.data);

export const deleteCustomerAddress = (customerId: string, id: number) =>
  axiosInstance.delete<void>(`/customer/${customerId}/address/${id}`).then((response) => response.data);


export const createApiAccess = (id: string) =>
  axiosInstance.post<ApiAccessResultModel>(`/customer/${id}/api`).then((response) => response.data);

export const deleteApiAccess = (id: string) =>
  axiosInstance.delete<void>(`/customer/${id}/api`).then((response) => response.data);

export const updateCurrentCustomerLanguage = (language: string) =>
  axiosInstance
    .put<void>('/customer/language', language, {
      headers: { 'Content-Type': 'text/plain' },
    })
    .then((response) => response.data);

export const impersonateCustomer = (customerId: string) =>
  axiosInstance.post<void>(`/customer/${customerId}/impersonate`);

export const getPasswordResetLink = (customerId: string) =>
  axiosInstance.post<{ link: string }>(`/customer/${customerId}/password-reset-link`).then((response) => response.data);

export const getActiveContractsAll = (query: string) =>
  axiosInstance
    .get<CustomerActiveContractExtended[]>('/customer/active-contracts-all', {
      params: { query },
    })
    .then((response) => response.data);

export const updateCustomerRoles = (customerId: string, roles: RoleType[]) =>
  axiosInstance
    .put<void>(`/customer/${customerId}/roles`, roles)
    .then((response) => response.data);
